import React, { useMemo, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveTreeMap } from '@nivo/treemap'

const colorPalette = [
  '#FF6B6B',
  '#4D96FF',
  '#FFB347',
  '#9B5DE5',
  '#00BBF9',
  '#F15BB5',
  '#FF9F1C',
  '#6C8CFF',
  '#845EC2',
  '#FF6F91',
  '#FFD166',
  '#7FDBFF',
  '#FF7F50',
  '#B39DDB',
  '#00B8A9',
  '#FF8FAB',
  '#F4A261',
  '#5E60CE',
  '#F72585',
  '#4CC9F0'
]

const hashString = (value) => {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

const buildUsersSignature = (users) => {
  return users
    .map((user, index) => {
      const id = String(user.chat_id || user.id || user.name || `user-${index}`)
      const value = Number(user.total_crystal ?? user.total ?? user.crystal ?? user.bet ?? 0)
      const percent = Number(user.percent || 0)
      return `${id}:${value}:${percent}`
    })
    .sort()
    .join('|')
}

const RoyaleParticipantsShowcase = ({
  users = [],
  totalBet = 0,
  monochrome = false,
  highlightId = '',
  highlightIndex = -1,
  containerClassName = 'w-full aspect-square'
}) => {
  const { t } = useTranslation()
  const rawClipPrefix = useId()
  const clipPrefix = useMemo(() => `clip-${hashString(rawClipPrefix)}`, [rawClipPrefix])
  const usersSignature = useMemo(() => buildUsersSignature(users), [users])
  const segments = useMemo(() => {
    const totalFromUsers = users.reduce(
      (sum, user) => sum + Number(user.total_crystal ?? user.total ?? user.crystal ?? user.bet ?? 0),
      0
    )
    const baseTotal = Number(totalBet || 0) > 0 ? Number(totalBet) : totalFromUsers

    const prepared = users.map((user, index) => {
      const rawValue = Number(user.total_crystal ?? user.total ?? user.crystal ?? user.bet ?? 0)
      const percentFromApi = Number(user.percent || 0)
      const percent = percentFromApi > 0 ? percentFromApi : baseTotal > 0 ? (rawValue / baseTotal) * 100 : 0
      const stableId = String(user.chat_id || user.id || user.name || `user-${index}`)
      const colorIndex = hashString(stableId) % colorPalette.length

      return {
        ...user,
        stableId,
        originalIndex: index,
        percent,
        color: colorPalette[colorIndex]
      }
    })

    const totalPercent = prepared.reduce((sum, item) => sum + item.percent, 0)
    if (totalPercent > 0) {
      return prepared
        .map((item) => ({
        ...item,
        percent: (item.percent / totalPercent) * 100
      }))
        .sort((a, b) => a.stableId.localeCompare(b.stableId))
    }

    return prepared.sort((a, b) => a.stableId.localeCompare(b.stableId))
  }, [usersSignature, totalBet])

  const treemapData = useMemo(() => {
    const minPercent = 7.5
    const neutralColor = '#2A2F36'
    const highlightColor = '#00FF7F'
    return {
      name: 'root',
      children: segments.map((segment) => {
        // Подсветка победителя: сначала по индексу (надёжно даже при пустых name/chat_id),
        // затем по строковому id для обратной совместимости
        const isHighlighted = (highlightIndex >= 0 && segment.originalIndex === highlightIndex)
          || (Boolean(highlightId) && segment.stableId === highlightId)
        return {
        id: segment.stableId,
        name: segment.name || segment.stableId,
        value: Math.max(minPercent, segment.percent || 0),
        color: isHighlighted
          ? highlightColor
          : monochrome
            ? neutralColor
            : segment.color,
        percent: segment.percent,
        avatar: segment.ava || `https://ui-avatars.com/api/?name=${segment.name}&background=00FF7F&color=0A0A0F`
        }
      })
    }
  }, [segments, monochrome, highlightId, highlightIndex])

  if (!segments.length) {
    return (
      <div className={`rounded-2xl overflow-hidden relative bg-gradient-to-br from-[#0f1b1a] via-[#0c1316] to-[#1a1412] border border-white/10 ${containerClassName}`}>
        <div className="absolute inset-0">
          <div className="absolute -top-10 -left-10 w-32 h-32 rounded-full bg-accent-primary/20 blur-2xl" />
          <div className="absolute bottom-6 right-6 w-24 h-24 rounded-full bg-[#4DDCFF]/20 blur-2xl" />
        </div>
        <div className="relative z-10 flex items-center justify-center h-full text-center px-6">
          <div>
            <p className="text-white text-lg font-semibold">{t('royale.participants.emptyTitle')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-0">
      <div className={`rounded-2xl overflow-hidden relative bg-surface/40 ${containerClassName}`}>
        <ResponsiveTreeMap
          data={treemapData}
          identity="id"
          value="value"
          innerPadding={3}
          outerPadding={3}
          enableParentLabel={false}
          isInteractive={false}
          animate={false}
          colors={(node) => node.data.color}
          borderColor={monochrome ? 'rgba(255,255,255,0.12)' : { from: 'color', modifiers: [['darker', 0.4]] }}
          nodeComponent={({ node }) => {
            if (node.depth === 0) return null

            const avatarSize = Math.max(18, Math.min(node.width, node.height) * 0.4)
            const centerX = node.x + node.width / 2
            const centerY = node.y + node.height / 2
            const clipId = `avatar-${clipPrefix}-${hashString(String(node.data.id))}`

            return (
              <g>
                <rect x={node.x} y={node.y} width={node.width} height={node.height} fill={node.color} />
                <defs>
                  <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
                    <circle cx={centerX} cy={centerY} r={avatarSize / 2} />
                  </clipPath>
                </defs>
                <image
                  href={node.data.avatar}
                  x={centerX - avatarSize / 2}
                  y={centerY - avatarSize / 2}
                  width={avatarSize}
                  height={avatarSize}
                  preserveAspectRatio="xMidYMid slice"
                  clipPath={`url(#${clipId})`}
                />
              </g>
            )
          }}
        />
      </div>
    </div>
  )
}

const areEqual = (prevProps, nextProps) => {
  if (prevProps.totalBet !== nextProps.totalBet) return false
  if (prevProps.monochrome !== nextProps.monochrome) return false
  if (prevProps.highlightId !== nextProps.highlightId) return false
  if (prevProps.highlightIndex !== nextProps.highlightIndex) return false
  if (prevProps.containerClassName !== nextProps.containerClassName) return false
  return buildUsersSignature(prevProps.users) === buildUsersSignature(nextProps.users)
}

export default React.memo(RoyaleParticipantsShowcase, areEqual)
